import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';

/**
 * usePlan()
 * --------
 * Small hook that fetches the current user's plan + usage snapshot from the
 * backend. Used to render:
 *   - Usage progress bars ("Scans: 12 / 25")
 *   - Upgrade CTAs on the sidebar
 *   - Disabled/blocked states on "New project" etc.
 *
 * Exposes:
 *   { plan_key, plan_name, limits, usage, loading, refresh() }
 */
export function usePlan() {
  const [data, setData] = useState({
    plan_key: null,
    plan_name: null,
    limits: null,
    usage: null,
    loading: true,
  });

  const refresh = useCallback(async () => {
    try {
      const r = await api.get('/billing/usage');
      setData({
        plan_key: r.data.plan_key,
        plan_name: r.data.plan_name,
        limits: r.data.limits,
        usage: r.data.usage,
        loading: false,
      });
    } catch (_e) {
      setData((d) => ({ ...d, loading: false }));
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return { ...data, refresh };
}

/** Format an X / Y usage string, treating null limit as "unlimited". */
export function formatUsage(used, limit) {
  if (limit === null || limit === undefined) return `${used ?? 0} / ∞`;
  return `${used ?? 0} / ${limit}`;
}

/** Return a 0..1 progress fraction, clamped. Null limit → 0 (no bar). */
export function usageFraction(used, limit) {
  if (!limit) return 0;
  const f = (used ?? 0) / limit;
  return Math.max(0, Math.min(1, f));
}
